import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, Row, Col } from 'react-bootstrap';
import type { FormValues } from '../CustomerWizard';

const ReviewStep: React.FC = () => {
  const { watch, formState: { errors } } = useFormContext<FormValues>();
  const formData = watch();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const formatPercentage = (value: number) => {
    if (value === undefined || value === null) return 'Not specified';
    return `${value}%`;
  };

  const renderError = (field: keyof FormValues | string) => {
    const error = errors[field as keyof FormValues];
    if (error) {
      return <div className="text-danger mt-1">{error.message}</div>;
    }
    return null;
  };

  return (
    <div className="review-step">
      <h4 className="mb-4">Review Customer Information</h4>

      {/* Basic Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Basic Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Customer Number:</strong> {formData.customerNumber}</p>
              <p><strong>Status:</strong> {formData.status}</p>
              <p><strong>Preferred Communication Channel:</strong> {formData.preferredCommChannel}</p>
              {renderError('customerNumber')}
              {renderError('status')}
              {renderError('preferredCommChannel')}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Company Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Company Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Registered Name:</strong> {formData.partyInfo.registeredName}</p>
              <p><strong>Trading Name:</strong> {formData.partyInfo.tradingName || 'Not specified'}</p>
              <p><strong>Registration Number:</strong> {formData.partyInfo.registrationNumber}</p>
              <p><strong>Industry Sector:</strong> {formData.partyInfo.industrySector || 'Not specified'}</p>
              {renderError('partyInfo.registeredName')}
              {renderError('partyInfo.registrationNumber')}
            </Col>
            <Col md={6}>
              <p><strong>Tax Number:</strong> {formData.partyInfo.taxNumber || 'Not specified'}</p>
              <p><strong>VAT Number:</strong> {formData.partyInfo.vatNumber || 'Not specified'}</p>
              <p><strong>Business Activity:</strong> {formData.partyInfo.businessActivity || 'Not specified'}</p>
              <p><strong>Annual Turnover:</strong> {formData.partyInfo.annualTurnover || 'Not specified'}</p>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <p><strong>Employee Count:</strong> {formData.partyInfo.employeeCount || 'Not specified'}</p>
              <p><strong>Establishment Date:</strong> {formatDate(formData.partyInfo.establishmentDate || '')}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Addresses */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Addresses</h5>
        </Card.Header>
        <Card.Body>
          {formData.addresses.map((address, index) => (
            <div key={index} className="mb-4">
              <h6>Address {index + 1}</h6>
              <Row>
                <Col md={6}>
                  <p><strong>Type:</strong> {address.type}</p>
                  <p><strong>Street Address:</strong> {address.line1}</p>
                  <p><strong>Suburb:</strong> {address.line2 || 'Not specified'}</p>
                  {renderError(`addresses.${index}.line1`)}
                  {renderError(`addresses.${index}.city`)}
                </Col>
                <Col md={6}>
                  <p><strong>City:</strong> {address.city}</p>
                  <p><strong>State/Province:</strong> {address.province || 'Not specified'}</p>
                  <p><strong>Postal Code:</strong> {address.postalCode}</p>
                  <p><strong>Country:</strong> {address.country}</p>
                  {renderError(`addresses.${index}.country`)}
                </Col>
              </Row>
            </div>
          ))}
          {renderError('addresses')}
        </Card.Body>
      </Card>

      {/* Contacts */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Contact Information</h5>
        </Card.Header>
        <Card.Body>
          {formData.contacts.map((contact, index) => (
            <div key={index} className="mb-3">
              <Row>
                <Col md={6}>
                  <p><strong>Type:</strong> {contact.contactType}</p>
                  <p><strong>Contact Value:</strong> {contact.contactValue}</p>
                  <p><strong>Primary:</strong> {contact.primary ? 'Yes' : 'No'}</p>
                  {renderError(`contacts.${index}.contactValue`)}
                </Col>
              </Row>
            </div>
          ))}
          {renderError('contacts')}
        </Card.Body>
      </Card>

      {/* Bank Details */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Bank Account Details</h5>
        </Card.Header>
        <Card.Body>
          {formData.bankDetails.map((bank, index) => (
            <div key={index} className="mb-4">
              <h6>Bank Account {index + 1}</h6>
              <Row>
                <Col md={6}>
                  <p><strong>Account Holder Name:</strong> {bank.accountHolderName}</p>
                  <p><strong>Bank Name:</strong> {bank.bankName}</p>
                  <p><strong>Account Number:</strong> {bank.accountNumber}</p>
                  {renderError(`bankDetails.${index}.accountHolderName`)}
                  {renderError(`bankDetails.${index}.bankName`)}
                  {renderError(`bankDetails.${index}.accountNumber`)}
                </Col>
                <Col md={6}>
                  <p><strong>Account Type:</strong> {bank.accountType}</p>
                  <p><strong>Branch Code:</strong> {bank.branchCode || 'Not specified'}</p>
                </Col>
              </Row>
            </div>
          ))}
          {renderError('bankDetails')}
        </Card.Body>
      </Card>

      {/* Directors */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Company Directors</h5>
        </Card.Header>
        <Card.Body>
          {formData.partyInfo.directors?.map((director, index) => (
            <div key={index} className="mb-4">
              <h6>Director {index + 1}</h6>
              <Row>
                <Col md={6}>
                  <p><strong>Name:</strong> {director.firstName} {director.lastName}</p>
                  <p><strong>ID Number:</strong> {director.idNumber}</p>
                  <p><strong>Nationality:</strong> {director.nationality || 'Not specified'}</p>
                  {renderError(`partyInfo.directors.${index}.firstName`)}
                  {renderError(`partyInfo.directors.${index}.lastName`)}
                  {renderError(`partyInfo.directors.${index}.idNumber`)}
                </Col>
                <Col md={6}>
                  <p><strong>Date of Birth:</strong> {formatDate(director.dateOfBirth || '')}</p>
                  <p><strong>Designation:</strong> {director.designation || 'Not specified'}</p>
                  <p><strong>Ownership Percentage:</strong> {formatPercentage(director.ownershipPercentage || 0)}</p>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <p><strong>Executive Director:</strong> {director.isExecutive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          ))}
          {renderError('partyInfo.directors')}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ReviewStep;

 